import React, { useEffect, useState } from "react";
import { Avatar, Box, Chip, Skeleton, Tooltip, Typography } from "@mui/material";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import EventRepeatOutlined from "@mui/icons-material/EventRepeatOutlined";
import { getYears } from "../../../services/CollaborateurService";
import { getSoldeYear } from "../../../services/CongeService";
import ConsoEditor from "./ConsoEditor";
import MovementsList from "./MovementsList";

const card = { borderRadius: "14px", background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(15,23,42,0.05)" };
const fmt = (n) => (Number.isInteger(n) ? n : Number(n || 0).toFixed(1).replace(".", ","));

const Stat = ({ label, value, Icon, color, bg, hint }) => (
    <Tooltip title={hint || ""} placement="top">
        <Box sx={{ ...card, flex: "1 1 150px", px: 1.75, py: 1.25, display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", background: bg, color }}>
                <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.1 }}>
                    {fmt(value ?? 0)} <Box component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#94a3b8" }}>j</Box>
                </Typography>
                <Typography sx={{ fontSize: "0.76rem", color: "#64748b" }}>{label}</Typography>
            </Box>
        </Box>
    </Tooltip>
);

export default function CongeCard({ collaborateur }) {
    const [years, setYears] = useState([new Date().getFullYear()]);
    const [year, setYear] = useState(new Date().getFullYear());
    const [solde, setSolde] = useState(null);
    const [loading, setLoading] = useState(false);
    const [movementsKey, setMovementsKey] = useState(0);

    useEffect(() => {
        getYears()
            .then(({ data }) => {
                const ys = [...(data?.years || data || [])].map(Number).sort((a, b) => b - a);
                if (ys.length) {
                    setYears(ys);
                    setYear(ys[0]);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!collaborateur || !year) return;
        const id = collaborateur.uuid ?? collaborateur.id;
        if (!id) return;
        setLoading(true);
        getSoldeYear(id, year)
            .then(({ data }) => {
                const ouverture = data.ouverture ?? data.solde_ouverture ?? data.soldeOuverture ?? 0;
                const acquis = data.acquis ?? 0;
                const consomme = data.consomme ?? 0;
                const reporte = data.reporte ?? 0;
                const ajustement = data.ajustement ?? 0;
                const disponible = data.disponible ?? (ouverture + acquis + reporte + ajustement - consomme);
                setSolde({ ouverture, acquis, consomme, reporte, ajustement, disponible });
            })
            .catch(() => setSolde(null))
            .finally(() => setLoading(false));
    }, [collaborateur, year]);

    if (!collaborateur) return null;

    const droits = (solde?.ouverture ?? 0) + (solde?.acquis ?? 0) + (solde?.reporte ?? 0) + (solde?.ajustement ?? 0);
    const used = solde?.consomme ?? 0;
    const pct = droits > 0 ? Math.min(100, (used / droits) * 100) : 0;
    const name = `${collaborateur.prenom ?? ""} ${collaborateur.nom ?? ""}`.trim();
    const id = collaborateur.uuid ?? collaborateur.id;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ ...card, p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Avatar sx={{ width: 46, height: 46, fontWeight: 700, bgcolor: "#ebf4fb", color: "#03407b", border: "1px solid #aed6f1" }}>
                    {`${collaborateur.prenom?.[0] || ""}${collaborateur.nom?.[0] || ""}`.toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "1.02rem", fontWeight: 800, color: "#0f172a" }}>{name}</Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>{collaborateur.email || collaborateur.fonction || ""}</Typography>
                </Box>
                <Box sx={{ ml: { md: "auto" }, display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mr: 0.25 }}>Année</Typography>
                    {years.map((y) => (
                        <Chip key={y} label={y} size="small" onClick={() => setYear(y)}
                            sx={{
                                height: 26, fontWeight: 700,
                                color: year === y ? "#fff" : "#475569",
                                background: year === y ? "#03407b" : "#f1f5f9",
                                "&:hover": { background: year === y ? "#02357a" : "#e2e8f0" },
                            }} />
                    ))}
                </Box>
            </Box>

            {loading ? (
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: "14px" }} />
            ) : (
                <Box sx={{ ...card, p: 2, display: "flex", flexDirection: "column", gap: 1.75 }}>
                    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>Solde {year}</Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#64748b" }}>
                            <b style={{ color: "#0f172a" }}>{fmt(used)} j</b> utilisés sur <b style={{ color: "#0f172a" }}>{fmt(droits)} j</b> de droits
                        </Typography>
                    </Box>
                    <Box sx={{ height: 12, borderRadius: 6, background: "#dcfce7", overflow: "hidden" }}>
                        <Box sx={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#b91c1c" : "linear-gradient(90deg, #f97316, #ef4444)", borderRadius: 6, transition: "width 0.4s ease" }} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <Stat label="Acquis" value={solde?.acquis} Icon={CheckCircleOutlineRoundedIcon} color="#166534" bg="#f0fdf4" hint="Jours gagnés sur l'année" />
                        {(solde?.reporte || 0) !== 0 && (
                            <Stat label="Reportés" value={solde?.reporte} Icon={EventRepeatOutlined} color="#5b21b6" bg="#f5f3ff" hint="Reliquat de l'année précédente" />
                        )}
                        <Stat label="Consommés" value={solde?.consomme} Icon={LocalFireDepartmentRoundedIcon} color="#b91c1c" bg="#fef2f2" hint="Jours déjà posés" />
                        <Stat label="Disponibles" value={solde?.disponible} Icon={HourglassBottomRoundedIcon} color="#03407b" bg="#ebf4fb" hint="Jours restant à poser" />
                    </Box>
                    {droits === 0 && (
                        <Typography sx={{ fontSize: "0.8rem", color: "#b45309", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", px: 1.25, py: 0.75 }}>
                            Aucun droit à congé enregistré pour {year}. Vérifiez la date d'intégration et le contrat du collaborateur.
                        </Typography>
                    )}
                </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.2fr) minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
                <ConsoEditor
                    collaborateurId={id}
                    year={year}
                    solde={solde}
                    onOptimisticUpdate={(updater) => setSolde(updater)}
                    onSaved={() => setMovementsKey((k) => k + 1)}
                />
                <MovementsList key={`${id}-${year}-${movementsKey}`} collaborateurId={id} year={year} height={300} />
            </Box>
        </Box>
    );
}
